import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Loading } from 'carbon-components-react';
import { isEmpty } from 'lodash';
import { ChevronLeft, Save } from 'lucide-react';
// Components
import { Container, ContentContainer } from '../../components/Wrapper/Wrapper';
import DetailOverview from './DetailSections/DetailOverview';
import DetailDates from './DetailSections/DetailDates';
import DetailManual from './DetailSections/DetailManual';
import DetailConneX from './DetailSections/DetailConneX';
import StudyPageImage from './DetailSections/StudyPageImage';
import ConfirmModal from '../../components/Modal/ConfirmModal';
// hooks
import useRegistriesGrid from '../Registries/useRegistriesGrid'
import useAttachmentsGrid from '../Attachments/useAttachmentsGrid'
import useLocationsGrid from '../Locations/useLocationsGrid'
import useLinksGrid from '../StudyLink/useLinksGrid';
// api/data
import useStudyData from '../../helpers/useStudyData';
import { useTenantConfig } from '../../context/TenantConfigContext';
import useReferenceData from '../../helpers/useReferenceData';
// styles
import './StudyDetails.scss';

const TAB_KEYS = ['overview', 'manual', 'logos', 'ctg', 'registries', 'attachments', 'locations'];
const TAB_LABELS = ['Overview', 'Manual Data', 'Logos/Buttons', 'CTG Data', 'Registries', 'Attachments', 'Locations'];

const StudyDetails = () => {

  // study ID from URL
  const studyUid = (() => {
    const url = window.location.href.split("/")
    return url[url.length - 1]
  })()

  const { config } = useTenantConfig();

  // study data
  const {
    // CURRENT DATA
    studyDetails,
    setStudyDetails,
    getFieldValue,
    getStudyData,
    // SET DATA
    dataToSubmit,
    setDataToSubmit,
    dirtyFields,
    setDirtyFields,
    // reducer action types
    INIT_STUDY,
    CLEAR,
    SET_OVERRIDE_DATA,
    SET_SPONSOR_DATA,
    SET_RELATED_STUDIES,
    SET_COMMERCIAL_PRODUCTS,
    SET_DEVELOPMENT_STATUS,
    // SAVE DATA
    submitData,
    submitConneXData,
    submitManualData,
    submitBooleanField,
    fieldStatuses,
    //SYNC DATA
    syncStudy,
  } = useStudyData(studyUid)

  // reference data (fetched once, passed to tabs)
  const {
    simpleStudyList,
    relatedStudyOptions,
    commercialProducts,
    developmentStatuses,
    conditionOptions,
    interventionOptions,
    studyListOptions,
  } = useReferenceData(studyUid)

  const [studyId, setStudyId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openSyncModal, setOpenSyncModal] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [pendingTab, setPendingTab] = useState(null);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);

  // registries
  const {
    addRegistryButton,
    registriesGrid,
  } = useRegistriesGrid({
    isStudyDetail: true,
    studyRegistry: studyDetails?.study_registries,
    setStudyDetails,
    studyPgId: studyId,
    studyOrgStudyId: studyDetails?.org_study_id,
    studyUid: studyDetails?.uid,
    refreshStudyData: getStudyData,
    studyListOptions,
  })

  // attachments
  const {
    addAttachmentsButton,
    attachmentGrid,
  } = useAttachmentsGrid({
    isStudyDetail: true,
    attachmentList: studyDetails?.attachments,
    setStudyDetails,
    studyPgId: studyId,
    studyOrgStudyId: studyDetails?.org_study_id,
    studyUid: studyDetails?.uid,
    refreshStudyData: getStudyData,
    studyListOptions,
  })

  // locations
  const {
    locationsGrid,
    addLocationButton,
    locationsLoading,
  } = useLocationsGrid({
    isStudyDetail: true,
    studyLocation: studyDetails?.locations,
    setStudyDetails,
    studyPgId: studyId,
    studyOrgStudyId: studyDetails?.org_study_id,
    studyListOptions,
  })

  const {
    linkGrid,
    addLinksButton,
    linksLoading,
  } = useLinksGrid({
    isStudyDetail: true,
    linkList: studyDetails?.links,
    setStudyDetails,
    studyPgId: studyId,
    studyOrgStudyId: studyDetails?.org_study_id,
    studyListOptions,
  })

  const { client, id } = useParams();

  useEffect(() => {
    if (studyDetails) {
      setStudyId(parseInt(studyDetails.id))
    }
  }, [studyDetails])

  // Warn on browser close/refresh with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (dirtyFields && !isEmpty(dirtyFields)) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [dirtyFields]);

  const confirmSync = () => {
    setOpenSyncModal(false);
    setLoading(true);
    syncStudy().then(() => {
      setLoading(false);
    });
  };

  const handleTabChange = (tabKey) => {
    if (dirtyFields && !isEmpty(dirtyFields)) {
      setPendingTab(tabKey);
      setShowUnsavedModal(true);
    } else {
      setActiveTab(tabKey);
    }
  };

  const confirmTabChange = () => {
    setDirtyFields({ type: CLEAR });
    setActiveTab(pendingTab);
    setPendingTab(null);
    setShowUnsavedModal(false);
  };

  const onOverviewSubmit = (id, value) => {
    submitBooleanField(id, value)
    if (id === 'org_study_id') {
      setStudyDetails({ type: INIT_STUDY, data: { ...studyDetails, [id]: value } })
    }
  }

  /** SAVE BUTTON LOGIC */

  const getSaveAction = () => {
    switch (activeTab) {
      case 'overview':
        return null; // overview uses auto-save toggles
      case 'manual':
        return {
          label: 'Save Manual Data',
          action: submitManualData,
          disabled: !dirtyFields || (
            (!dirtyFields.sponsor_data || isEmpty(dirtyFields.sponsor_data)) &&
            !dirtyFields.related_studies &&
            !dirtyFields.commercial_products &&
            !dirtyFields.development_status &&
            !dirtyFields.logo
          ),
        };
      case 'ctg':
        return {
          label: 'Save Override Data',
          action: submitConneXData,
          disabled: !dirtyFields || !dirtyFields.override || isEmpty(dirtyFields.override),
        };
      default:
        return null; // grid tabs (logos, registries, attachments, locations) don't need a save button
    }
  };

  const saveAction = getSaveAction();

  /** TAB CONTENT */

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <>
            <div className="study-details__section">
              <h2 className="study-details__section-title">Overview</h2>
              <div style={{ marginTop: 20 }}>
                <DetailOverview
                  data={studyDetails}
                  submit={onOverviewSubmit}
                />
              </div>
            </div>
            <div className="study-details__section">
              <h2 className="study-details__section-title">Study Dates</h2>
              <div style={{ marginTop: 20 }}>
                <DetailDates studyDetails={studyDetails} />
              </div>
            </div>
          </>
        );

      case 'manual':
        return (
          <div className="study-details__section">
            <div className="study-details__section-header">
              <div>
                <h2 className="study-details__section-title">Manually Entered Data</h2>
                <p className="study-details__section-desc">
                  Study data that is manually entered and not pulled from CTG. For manually entered location fields, go to the Locations section.
                </p>
              </div>
            </div>
            <DetailManual
              data={studyDetails}
              dirtyFields={dirtyFields}
              dataToSubmit={dataToSubmit}
              setDataToSubmit={setDataToSubmit}
              SET_SPONSOR_DATA={SET_SPONSOR_DATA}
              SET_RELATED_STUDIES={SET_RELATED_STUDIES}
              SET_DEVELOPMENT_STATUS={SET_DEVELOPMENT_STATUS}
              SET_COMMERCIAL_PRODUCTS={SET_COMMERCIAL_PRODUCTS}
              fieldStatuses={fieldStatuses}
              simpleStudyList={simpleStudyList}
              relatedStudyOptions={relatedStudyOptions}
              commercialProducts={commercialProducts}
              developmentStatuses={developmentStatuses}
            />
          </div>
        );

      case 'logos':
        return (
          <>
            {config.enableStudyPageImage && (
              <div className="study-details__section">
                <div className="study-details__section-header">
                  <h2 className="study-details__section-title">Study Page Image</h2>
                </div>
                <StudyPageImage
                  studyId={studyDetails?.id}
                  currentImage={studyDetails?.logo}
                  onUpdate={(data) => setStudyDetails({ type: INIT_STUDY, data })}
                />
              </div>
            )}
            <div className="study-details__section">
              <div className="study-details__section-header">
                <h2 className="study-details__section-title">Logos / Buttons</h2>
                {addLinksButton()}
              </div>
              <div className="study-details__section-grid-content">
                {linkGrid()}
              </div>
            </div>
          </>
        );

      case 'ctg':
        return (
          <>
            <div className="study-details__section">
              <div className="study-details__section-header">
                <div>
                  <h2 className="study-details__section-title">ConneX Data</h2>
                  <p className="study-details__section-desc">
                    Study data pulled from CTG through ConneX. To override a field, enter values into the override column.
                  </p>
                </div>
              </div>
            </div>
            <DetailConneX
              data={studyDetails}
              getFieldValue={getFieldValue}
              dirtyFields={dirtyFields}
              dataToSubmit={dataToSubmit}
              setDataToSubmit={setDataToSubmit}
              SET_OVERRIDE_DATA={SET_OVERRIDE_DATA}
              conditionOptions={conditionOptions}
              interventionOptions={interventionOptions}
            />
          </>
        );

      case 'registries':
        return (
          <div className="study-details__section">
            <div className="study-details__section-header">
              <h2 className="study-details__section-title">Registries</h2>
              {addRegistryButton()}
            </div>
            <div className="study-details__section-grid-content">
              {registriesGrid()}
            </div>
          </div>
        );

      case 'attachments':
        return (
          <div className="study-details__section">
            <div className="study-details__section-header">
              <h2 className="study-details__section-title">Attachments</h2>
              {addAttachmentsButton()}
            </div>
            <div className="study-details__section-grid-content">
              {attachmentGrid()}
            </div>
          </div>
        );

      case 'locations':
        return (
          <div className="study-details__section">
            <div className="study-details__section-header">
              <h2 className="study-details__section-title">Locations</h2>
              {addLocationButton()}
            </div>
            <div className="study-details__section-grid-content">
              {locationsGrid()}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (!studyDetails) {
    return <Loading />
  }

  return (
    <Container>
      <ContentContainer>
        {loading && <Loading />}

        <div className="study-details">
          <Link to={`/${client}/studies`} className="study-details__back">
            <ChevronLeft />
            Back to All Studies
          </Link>

          <h1 className="study-details__title">
            Org Study ID: {studyDetails?.org_study_id}
          </h1>

          <div className="study-details__tab-bar">
            <div className="study-details__tab-bar-inner">
              <div className="study-details__tabs">
                {TAB_KEYS.map((key, index) => (
                  <button
                    key={key}
                    className={`study-details__tab${activeTab === key ? ' study-details__tab--active' : ''}`}
                    onClick={() => handleTabChange(key)}
                  >
                    {TAB_LABELS[index]}
                  </button>
                ))}
              </div>
              {saveAction && (
                <button
                  className={`study-details__save-btn${saveAction.disabled ? ' study-details__save-btn--disabled' : ''}`}
                  onClick={saveAction.action}
                  disabled={saveAction.disabled}
                >
                  <Save /> {saveAction.label}
                </button>
              )}
            </div>
          </div>

          {renderTabContent()}
        </div>

        <ConfirmModal
          title="Regenerate CTG Data"
          content={`Do you want to regenerate CTG study data for ${studyDetails?.org_study_id}?`}
          open={openSyncModal}
          onClose={() => setOpenSyncModal(false)}
          onConfirm={() => confirmSync()}
        />
        <ConfirmModal
          title="Unsaved Changes"
          content="You have unsaved changes. Are you sure you want to leave this tab without saving?"
          open={showUnsavedModal}
          onClose={() => { setShowUnsavedModal(false); setPendingTab(null); }}
          onConfirm={confirmTabChange}
        />
      </ContentContainer>
    </Container>
  );
};

export default StudyDetails;
